import { useTranslation } from 'react-i18next';
import { FileDown, FileSpreadsheet, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Button from '../ui/Button';
import { cn } from '../../utils/cn';
import type { ReportExportFormat } from './types';

/**
 * Export as three plain buttons rather than a dropdown.
 *
 * Three items do not earn a menu: a menu costs a click and hides the choice, and HR exports the
 * same format every month. The one that is running says so in place of its own label, so the
 * feedback is where the user clicked instead of only in a toast.
 */

const FORMAT_ICON: Record<ReportExportFormat, LucideIcon> = {
  xlsx: FileSpreadsheet,
  pdf: FileText,
  csv: FileDown,
};

const DEFAULT_FORMATS: ReportExportFormat[] = ['xlsx', 'pdf', 'csv'];

export interface ExportMenuProps {
  onExport: (format: ReportExportFormat) => void;
  /** The format currently being produced, or null. Every button is disabled while one runs. */
  busy?: ReportExportFormat | null;
  disabled?: boolean;
  formats?: ReportExportFormat[];
  className?: string;
  testId?: string;
}

export function ExportMenu({
  onExport,
  busy = null,
  disabled = false,
  formats = DEFAULT_FORMATS,
  className,
  testId,
}: ExportMenuProps) {
  const { t } = useTranslation();

  const LABEL: Record<ReportExportFormat, string> = {
    xlsx: t('reports.exportExcel'),
    pdf: t('reports.exportPdf'),
    csv: t('reports.exportCsv'),
  };

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      role="group"
      aria-label={t('reports.export')}
      data-testid={testId}
    >
      {formats.map((format) => {
        const Icon = FORMAT_ICON[format];
        const running = busy === format;
        return (
          <Button
            key={format}
            type="button"
            variant="outline"
            size="sm"
            loading={running}
            disabled={disabled || (busy !== null && !running)}
            onClick={() => onExport(format)}
            data-testid={`export-${format}`}
          >
            {!running && <Icon className="me-2 h-4 w-4 shrink-0" aria-hidden="true" />}
            {running ? t('reports.exporting') : LABEL[format]}
          </Button>
        );
      })}
    </div>
  );
}

export default ExportMenu;
